import { queryOptions } from '@tanstack/react-query';

import apiClient from '@/constants/http';
import type { ApiError } from '@/hooks/mutations/auth';
import {
  isNoResponse,
  readStatusCode,
  shouldRetryApiFailure,
} from '@/utils/apiErrors';
import { Logger } from '@/utils/logging';

import type {
  CustomerSaleQueryKey,
  CustomerSaleRequest,
  CustomerSaleResponse,
} from './types';

const fetchCustomerSale = async (
  requestData: CustomerSaleRequest,
): Promise<CustomerSaleResponse> => {
  try {
    const { data } = await apiClient.get<CustomerSaleResponse>(
      `/v1/customer-sale/${requestData.saleOrderIdOrAppointmentId}`,
      {
        params: {
          include: 'clientReferrals',
        },
      },
    );
    return data;
  } catch (error: unknown) {
    // 404: the link's sale, cart and appointment no longer exist.
    const log =
      isNoResponse(error) || readStatusCode(error) === 404
        ? Logger.warn
        : Logger.error;
    log('Error fetching customer sale', { error });
    // Parse error response from API
    const apiError: ApiError = {
      message:
        error instanceof Error
          ? error.message
          : 'Failed to fetch customer sale',
      code:
        typeof error === 'object' &&
        error !== null &&
        'code' in error &&
        typeof error.code === 'string'
          ? error.code
          : 'CUSTOMER_SALE_ERROR',
      details:
        typeof error === 'object' &&
        error !== null &&
        'details' in error &&
        error.details !== undefined
          ? error.details
          : undefined,
      // Forwarded so callers can distinguish a confirmed not-found sale from a
      // transient/network error (the restify error body carries its own
      // statusCode, same convention standby's own queries already read).
      statusCode:
        typeof error === 'object' &&
        error !== null &&
        'statusCode' in error &&
        typeof error.statusCode === 'number'
          ? error.statusCode
          : undefined,
    };
    throw apiError;
  }
};

export const customerSaleQueryOptions = (
  saleOrderIdOrAppointmentId: string,
) => {
  return queryOptions<
    CustomerSaleResponse,
    ApiError,
    CustomerSaleResponse,
    CustomerSaleQueryKey
  >({
    enabled: Boolean(saleOrderIdOrAppointmentId),
    queryKey: ['customer-sale', saleOrderIdOrAppointmentId],
    queryFn: () => fetchCustomerSale({ saleOrderIdOrAppointmentId }),
    retry: shouldRetryApiFailure,
  });
};
